import {useMemo} from 'react';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {useCustomerGroupsKey, useUI, useMedia} from 'coreHooks';
import {useCategoryActions} from 'coreHooks/categories';
import ViewportMgr from 'coreClasses/view/ViewportMgr';

import {getPersonalizedItemForCurrentCustomer} from 'brand/app/contentful/components/PersonalizedBlock/helpers';
import {FLAT_SUB_LISTS_CONTAINER_ID} from 'coreUtils/livePreview';
import {cloneObject} from 'coreUtils/utils';

export default (data: any, disableFlatNavigationOn: string = '>=large') => {
	const {managerRepository} = useGeneralProvider();
	const currentCustomer = managerRepository.getCustomerMgr().getCurrentCustomer();
	const categoryActions = useCategoryActions();
	const isServerUI = useUI('isServer');
	const media = useMedia();
	const isDesktop = media(disableFlatNavigationOn);
	const customerGroupsKey = useCustomerGroupsKey();

	return useMemo(() => {
		if (isServerUI || ViewportMgr.query(disableFlatNavigationOn)) {
			return null;
		}

		const flatNavigation = {
			sys: {
				id: FLAT_SUB_LISTS_CONTAINER_ID
			},
			__typename: 'FlatNavigationItemContainer',
			subItemsCollection: {
				items: []
			},
			subItems: {total: 0, __typename: 'FlatNavigationItemSubItemsCollection'},
			root: null
		};

		const parseItem = (i) => {
			if (i.__typename && i.__typename === 'NavigationItem') {
				i.__typename = 'FlatNavigationItem';
			}
		};

		const parseLevel = (item, level = 0) => {
			if (item?.subItemsCollection?.items) {
				const items = [];

				item?.subItemsCollection?.items?.forEach((i: any) => {
					if (i.__typename !== 'PersonalizedBlock') {
						parseItem(i);
						items.push(i);
						parseLevel(i, level + 1);
					} else {
						const personalizedItem = getPersonalizedItemForCurrentCustomer(
							i?.personalizedItemsCollection?.items || [],
							currentCustomer
						);

						if (personalizedItem?.item) {
							parseItem(personalizedItem?.item);
							items.push(personalizedItem?.item);
							parseLevel(personalizedItem?.item, level + 1);
						}
					}
				});

				if (items.length > 0) {
					const categoryItems = item?.category?.items || [];
					const categoryId = categoryItems.length ? categoryItems[0]?.id : null;
					const apiCategory = categoryId ? categoryActions.getCategory(categoryId) : null;
					const title = item.title || apiCategory?.getName() || '';
					const newItem = {
						__typename: 'FlatNavigationItem',
						sys: item.sys,
						targetUrl: item.targetUrl,
						targetPage: item.targetPage,
						category: item.category,
						viewAll: item.viewAll,
						image: item.image,
						imageTitle: item.image?.description,
						customSettings: item.customSettings,
						editorialContent: item.editorialContent,
						title,
						isFlatSubList: true,
						subItems: {total: items.length, __typename: 'FlatNavigationItemSubItemsCollection'},
						subItemsCollection: {
							items
						}
					};

					if (level > 0) {
						flatNavigation.subItemsCollection.items.push(newItem);
					} else {
						flatNavigation.root = newItem;
					}

					flatNavigation.subItems.total += 1;
				}
			}
		};

		const dataObject = data?.headerCollection?.items?.[0]?.navigation || data;

		if (dataObject) {
			parseLevel(cloneObject(dataObject));
		}

		return flatNavigation;
	}, [data, isServerUI, isDesktop, customerGroupsKey]);
};
